import React, { useState, useId } from 'react';
import {
  TableRow, TableCell, TextField, FormControl, InputLabel, Select, MenuItem,
  Switch, FormControlLabel, IconButton, Tooltip, Collapse, Box, Grid, Typography,
} from '@mui/material';
import {
  Delete as DeleteIcon,
  Settings as SettingsIcon,
} from '@mui/icons-material';
import { REGISTRY_FIELD_TYPES } from '../utils/registrySchemaUtils';

/**
 * One editable schema property. Emits partial patches via onChange (merged into
 * the row by the parent). Advanced metadata lives behind a per-row expander; the
 * reference configurator (target internal registry + field) shows for type=reference.
 */
const SchemaPropertyRow = ({ row, onChange, onRemove, registries = [] }) => {
  const [expanded, setExpanded] = useState(false);
  const fieldId = useId();

  const targetRegistry = registries.find((r) => r.id === row.reference?.registryId);
  const targetFields = targetRegistry
    ? Object.keys(targetRegistry.schema?.properties || {})
    : [];

  const setRef = (patch) =>
    onChange({ reference: { ...(row.reference || { registryId: '', field: '' }), ...patch } });

  return (
    <>
      <TableRow hover>
        <TableCell>
          <TextField
            label="Chiave" size="small" value={row.key}
            onChange={(e) => onChange({ key: e.target.value })}
          />
        </TableCell>
        <TableCell>
          <TextField
            label="Nome" size="small" value={row.name}
            onChange={(e) => onChange({ name: e.target.value })}
          />
        </TableCell>
        <TableCell>
          <FormControl size="small" sx={{ minWidth: 130 }}>
            <InputLabel id={`${fieldId}-type`}>Tipo</InputLabel>
            <Select
              labelId={`${fieldId}-type`} label="Tipo" value={row.type}
              onChange={(e) => onChange({ type: e.target.value })}
            >
              {REGISTRY_FIELD_TYPES.map((t) => (
                <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </TableCell>
        <TableCell align="center">
          <Switch
            checked={row.required}
            onChange={(e) => onChange({ required: e.target.checked })}
            inputProps={{ 'aria-label': 'Obbligatorio' }}
          />
        </TableCell>
        <TableCell align="right">
          <Tooltip title="Opzioni avanzate">
            <IconButton size="small" aria-label="Opzioni avanzate" onClick={() => setExpanded((v) => !v)}>
              <SettingsIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Rimuovi campo">
            <IconButton size="small" color="error" aria-label="Rimuovi campo" onClick={onRemove}>
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell colSpan={5} sx={{ py: 0, borderBottom: expanded || row.type === 'reference' ? undefined : 'none' }}>
          <Collapse in={expanded || row.type === 'reference'} timeout="auto" unmountOnExit>
            <Box sx={{ p: 2 }}>
              {row.type === 'reference' && (
                <Grid container spacing={2} sx={{ mb: expanded ? 2 : 0 }}>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small">
                      <InputLabel id={`${fieldId}-refreg`}>Anagrafica riferita</InputLabel>
                      <Select
                        labelId={`${fieldId}-refreg`} label="Anagrafica riferita"
                        value={row.reference?.registryId || ''}
                        onChange={(e) => setRef({ registryId: e.target.value, field: '' })}
                      >
                        {registries.map((r) => (
                          <MenuItem key={r.id} value={r.id}>{r.name || r.code}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <FormControl fullWidth size="small" disabled={!targetRegistry}>
                      <InputLabel id={`${fieldId}-reffield`}>Campo riferito</InputLabel>
                      <Select
                        labelId={`${fieldId}-reffield`} label="Campo riferito"
                        value={row.reference?.field || ''}
                        onChange={(e) => setRef({ field: e.target.value })}
                      >
                        {targetFields.map((f) => (
                          <MenuItem key={f} value={f}>{f}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>
              )}

              {expanded && (
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12}>
                    <Typography variant="subtitle2">Opzioni avanzate</Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={<Switch checked={row.searchable}
                        onChange={(e) => onChange({ searchable: e.target.checked })} />}
                      label="Ricercabile"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={<Switch checked={row.visibleOnUi}
                        onChange={(e) => onChange({ visibleOnUi: e.target.checked })} />}
                      label="Visibile in tabella"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <FormControlLabel
                      control={<Switch checked={row.isKey}
                        onChange={(e) => onChange({ isKey: e.target.checked })} />}
                      label="Chiave"
                    />
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <TextField
                      fullWidth size="small" type="number" label="Lunghezza massima"
                      value={row.maxLength}
                      onChange={(e) => onChange({ maxLength: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Pattern (regex)"
                      value={row.pattern}
                      onChange={(e) => onChange({ pattern: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth size="small" label="Valori enum (separati da virgola)"
                      value={row.enumText}
                      onChange={(e) => onChange({ enumText: e.target.value })}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth size="small" label="Descrizione"
                      value={row.description}
                      onChange={(e) => onChange({ description: e.target.value })}
                    />
                  </Grid>
                </Grid>
              )}
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
};

export default SchemaPropertyRow;
